import React, { useEffect, useMemo, useRef } from 'react';
import { GeoJSON, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';

export const FELLING_COLOR = '#dc2626';
export const VMD_PREVIEW_COLOR = '#2563eb';

const PM_OPTIONS = {
  allowSelfIntersection: false,
  snappable: true,
  snapDistance: 15,
  templineStyle: { color: FELLING_COLOR, weight: 2 },
  hintlineStyle: { color: FELLING_COLOR, dashArray: [5, 5] },
  pathOptions: { color: FELLING_COLOR, fillColor: FELLING_COLOR, fillOpacity: 0.25, weight: 2.5 }
};

const LV_TRANSLATION = {
  tooltips: {
    firstVertex: 'Klikšķiniet, lai sāktu cirsmas kontūru',
    continueLine: 'Klikšķiniet nākamo punktu',
    finishPoly: 'Klikšķiniet uz pirmā punkta, lai noslēgtu'
  }
};

export const DrawControl = ({ drawing, onCreated }) => {
  const map = useMap();
  const created = useRef(onCreated);
  created.current = onCreated;

  useEffect(() => {
    if (!map.pm) return undefined;
    map.pm.setLang('lv', LV_TRANSLATION, 'en');
    map.pm.setGlobalOptions(PM_OPTIONS);
    const handler = (e) => {
      const geometry = e.layer.toGeoJSON().geometry;
      map.removeLayer(e.layer);
      created.current?.(geometry);
    };
    map.on('pm:create', handler);
    return () => {
      map.off('pm:create', handler);
      map.pm.disableDraw();
    };
  }, [map]);

  useEffect(() => {
    if (!map.pm) return;
    if (drawing) map.pm.enableDraw('Polygon', PM_OPTIONS);
    else map.pm.disableDraw();
  }, [drawing, map]);

  return null;
};

const EditableArea = ({ area, selected, editing, onSelect, onEdited, canEdit, children }) => {
  const ref = useRef(null);
  const editedRef = useRef(onEdited);
  editedRef.current = onEdited;
  const data = useMemo(() => ({ type: 'Feature', properties: {}, geometry: area.geometry }), [area.geometry]);

  useEffect(() => {
    const group = ref.current;
    if (!group) return undefined;
    const layers = [];
    group.eachLayer((layer) => {
      if (!layer.pm && L.PM?.reInitLayer) L.PM.reInitLayer(layer);
      if (!layer.pm) return;
      layers.push(layer);
      if (editing) {
        layer.pm.enable({ allowSelfIntersection: false, snappable: true });
        layer.on('pm:edit', () => editedRef.current?.(area.id, layer.toGeoJSON().geometry));
      }
    });
    return () => layers.forEach((layer) => { layer.off('pm:edit'); layer.pm?.disable(); });
  }, [editing, area.id]);

  return (
    <GeoJSON
      ref={ref}
      key={`${area.id}-${area.updated_at}`}
      data={data}
      style={() => ({ color: FELLING_COLOR, weight: selected ? 4 : 2.5, dashArray: editing ? '6 4' : null, fillColor: FELLING_COLOR, fillOpacity: selected ? 0.35 : 0.2 })}
      eventHandlers={{ click: () => onSelect?.(area.id) }}
    >
      {!editing && children}
    </GeoJSON>
  );
};

export const FellingAreasLayer = ({ areas, selectedId, onSelect, editingId, onEdited, canEdit = false, renderPopup }) => areas.map((area) => (
  <EditableArea key={area.id} area={area} selected={selectedId === area.id} editing={editingId === area.id} onSelect={onSelect} onEdited={onEdited} canEdit={canEdit}>
    <Popup>
      <div className="min-w-[180px] space-y-1" data-testid={`felling-popup-${area.id}`}>
        <p className="font-semibold text-red-700 leading-tight">{area.name}</p>
        {area.project_name && <p className="text-xs text-gray-600">{area.project_name}</p>}
        {area.affirmation_number && <p className="text-xs">Apliecinājums Nr. <strong>{area.affirmation_number}</strong></p>}
        {area.area_ha ? <p className="text-xs">Platība: {area.area_ha} ha</p> : null}
        {area.notes && <p className="text-xs italic text-gray-600">{area.notes}</p>}
        {renderPopup?.(area)}
      </div>
    </Popup>
  </EditableArea>
));

export const PreviewGeoJsonLayer = ({ geojson, color = VMD_PREVIEW_COLOR }) => {
  if (!geojson) return null;
  return <GeoJSON key={JSON.stringify(geojson).length} data={geojson} style={() => ({ color, weight: 3, dashArray: '8 4', fillColor: color, fillOpacity: 0.2 })} />;
};
